import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useTheme, useLanguage, API } from '@/App';
import { getTranslation } from '@/translations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Check } from 'lucide-react';
import ThemeLanguageToggle from '@/components/ThemeLanguageToggle';
import GoogleAuthButton from '@/components/GoogleAuthButton';
import axios from 'axios';

const LoginPage = () => {
  const { login, user } = useAuth();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (window.location.hash.includes('google_error')) {
      toast.error(t('auth.googleLoginFailed'));
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  React.useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const loginResult = await login(email, password);
      toast.success(t('auth.loginSuccess'));
      
      // Check onboarding status after successful login
      try {
        const token = loginResult.token || localStorage.getItem('token');
        const onboardingResponse = await axios.get(`${API}/onboarding/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (onboardingResponse.data?.completed) {
          // Onboarding completed, go to dashboard
          navigate('/dashboard');
        } else {
          // Onboarding not completed, redirect to onboarding wizard
          navigate('/onboarding');
        }
      } catch (onboardingError) {
        // If onboarding check fails, go to dashboard as fallback
        console.error('Onboarding status check failed:', onboardingError);
        navigate('/dashboard');
      }
    } catch (error) {
      // Check if email is unverified (403 status)
      if (error.response?.status === 403) {
        const unverifiedEmail = error.response?.headers?.['x-unverified-email'] || email;
        toast.error('Email belum diverifikasi. Silakan cek inbox Anda.');
        navigate('/verify-pending', { state: { email: unverifiedEmail } });
        return;
      }
      toast.error(error.response?.data?.detail || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-500 to-indigo-600 p-12 flex-col justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img
            src="/images/logo-white-bg.png"
            alt="Logo"
            className="w-10 h-10 rounded-lg"
          />
          <span className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>Nerava</span>
        </Link>

        <div>
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
            {t('auth.welcomeBack')}
          </h1>
          <p className="text-blue-100 text-lg">
            {t('auth.welcomeBackDesc')}
          </p>
        </div>

        <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm dark:bg-zinc-900">
          <p className="text-white/90 mb-4">
            {t('auth.testimonialText')}
          </p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white dark:bg-zinc-900">
              <Check className="w-5 h-5" />
            </div>
            <div>
              <p className="text-white font-medium">{t('auth.testimonialName')}</p>
              <p className="text-blue-200 text-sm">{t('auth.testimonialRole')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white dark:bg-zinc-900">
        <div className="w-full max-w-md">
          <div className="mb-4 flex justify-end">
            {/* <ThemeLanguageToggle /> */}
          </div>
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Link to="/" className="flex items-center gap-2">
              <img src="/images/logo.png" alt="Nerava" className="w-10 h-10" />
              <span className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>Nerava</span>
            </Link>
          </div>

          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mb-2" style={{ fontFamily: 'Manrope' }}>{t('auth.loginToAccount')}</h2>
          <p className="text-slate-600 dark:text-zinc-400 mb-8">{t('auth.loginSubtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-700 dark:text-zinc-300 mb-2 block">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 dark:text-zinc-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.enterEmail')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 py-6 rounded-xl dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="password" className="text-slate-700 dark:text-zinc-300">{t('auth.password')}</Label>
                <Link to="/forgot-password" className="text-sm text-indigo-600 hover:underline dark:text-indigo-400" data-testid="forgot-password-link">
                  Lupa Password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 dark:text-zinc-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('auth.enterPassword')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 py-6 rounded-xl dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 dark:text-zinc-400"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-xl py-6 text-lg"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  {t('auth.login')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-slate-200 dark:bg-zinc-800" />
            <span className="text-xs uppercase tracking-wider text-slate-500 dark:text-zinc-500">{t('auth.orDivider')}</span>
            <div className="h-px flex-1 bg-slate-200 dark:bg-zinc-800" />
          </div>

          <GoogleAuthButton label={t('auth.continueWithGoogle')} testId="google-login-btn" />

          <p className="text-center text-slate-600 dark:text-zinc-400 mt-8">
            {t('auth.dontHaveAccount')}{' '}
            <Link to="/register" className="text-indigo-600 font-medium hover:underline dark:text-indigo-400" data-testid="register-link">
              {t('auth.signUpFree')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
